<script setup>
import { useStore } from 'vuex';
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import SideNavBarItem from '@/components/SideNavBarItem.vue';
import NavIcon from '@/components/NavIcon.vue';
import { useRoute, useRouter } from 'vue-router';
import { organisationMembers } from '@/api/organisation';
import useOpenResearchRequests from '@/composables/useOpenResearchRequests';
import { notifyError } from '@/utils/notification';
import { isPartnerOrganisation } from '@/utils/landing';
import { getAiDisabledReason } from '@/utils/aiAssistant';
import { getInitials } from '@/utils/string';
import { useI18n } from 'vue-i18n';
import adminEnvironmentHeaderLogo from '@/assets/icons/admin-environment/sm-light.svg';
import stramigoDefaultLogo from '@/assets/img/stramigo-face.png';
import CoinProgressBar from '@/components/reviewer/CoinProgressBar.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const user = computed(() => store.state.user);
const app = computed(() => store.state.app);
const showSideNav = computed(() => store.state.showSideNav);
const showSideNavCollapse = computed(() => store.state.showSideNavCollapse);
const isAdminEnvironment = computed(() => route.meta.app === 'admin');
// De reviewersomgeving: de pagina's van de respondent. Daar alleen de knoppen
// voor de respondent, plus de weg naar de bedrijfs- en de adminomgeving voor
// wie die heeft. Ook uitgelogd, want een deel van die pagina's is publiek.
const isReviewerEnvironment = computed(() => ['reviewer', 'public'].includes(route.meta.app));
const hasOrganisationMembership = computed(() => (
  (user.value.memberships?.length || 0) > 0
  || Boolean(user.value.org?.membership?.id)
));
const showOrganisationEnvironment = computed(() => (
  Boolean(user.value.org) && !isAdminEnvironment.value && !isReviewerEnvironment.value
));
const showAdminEnvironment = computed(() => Boolean(user.value.sust) && isAdminEnvironment.value);
const showEnvironmentSwitcher = computed(() => (
  Boolean(user.value.sust)
  && !isReviewerEnvironment.value
  && (!isAdminEnvironment.value || hasOrganisationMembership.value)
));
const isPartnerEnvironment = computed(() => isPartnerOrganisation(user.value.org));
const isAiInactive = computed(() => Boolean(getAiDisabledReason(user.value)));
// A partner's environment has no switcher: its dashboard is where you choose.
const hasMultipleMemberships = computed(() => (
  (user.value.memberships?.length || 0) > 1 && !isPartnerEnvironment.value
));
const isOrganisationMembershipUnverified = computed(() => (
  showOrganisationEnvironment.value
  && user.value.org?.membership?.id
  && !user.value.org?.membership?.is_verified
));
const sideNavClass = computed(() => {
  if (isAdminEnvironment.value) {
    return 'sidenav-admin';
  }
  // Licht, zodat je meteen ziet dat je in de reviewersomgeving bent en niet in
  // die van het bedrijf.
  return isReviewerEnvironment.value ? 'sidenav-reviewer' : 'sidenav-default';
});
// Het logo gaat naar het beginpunt van de omgeving; voor de reviewer is dat
// het dashboard.
const logoRoute = computed(() => (
  isReviewerEnvironment.value ? { name: 'public-dashboard' } : homeRoute.value
));
// Op de lichte sidebar van de reviewersomgeving de donkere iconen; de lichte
// zijn gemaakt voor een donkere achtergrond.
const withReviewerIcons = (items) => (
  isReviewerEnvironment.value
    ? items.map((item) => ({ ...item, imageLight: item.imageDark || item.imageLight }))
    : items
);
const sideNavLogo = computed(() => {
  if (!app.value?.logo_sm) {
    return stramigoDefaultLogo;
  }
  return require(`@/assets/img/logos/${app.value.logo_sm}`);
});
const sideNavLogoAlt = computed(() => app.value?.name || 'Stramigo');
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1600);
const isMobileViewport = computed(() => windowWidth.value < 1200);
const isDesktopViewport = computed(() => !isMobileViewport.value);
const isCollapsedDesktop = computed(() => isDesktopViewport.value && showSideNavCollapse.value);
const currentOrganisationName = computed(() => user.value.org?.name || '');
// Partners on top of the switcher, the rest in the order they come (by name):
// for a partner's colleagues the partner is where they start from, and a
// thin line sets it apart from its brands.
const switcherMemberships = computed(() => {
  const memberships = user.value.memberships || [];
  const partners = memberships.filter((membership) => isPartnerOrganisation(membership.organisation));
  const others = memberships.filter((membership) => !isPartnerOrganisation(membership.organisation));
  return [
    ...partners.map((membership, index) => ({
      membership,
      dividerAfter: index === partners.length - 1 && others.length > 0,
    })),
    ...others.map((membership) => ({ membership, dividerAfter: false })),
  ];
});
// An organisation without a logo gets a tile with its initials, so a partner
// and each of its brands still look different in the side nav.
const organisationInitials = computed(() => getInitials(user.value.org?.name));
const sideNavToggleIcon = computed(() => {
  if (isMobileViewport.value) {
    return showSideNav.value ? 'times' : 'bars';
  }
  return showSideNavCollapse.value ? 'angle-right' : 'angle-left';
});
// In a brand the user reaches through a partner, its partner membership.
const brandPartnerMembership = computed(() => {
  if (!showOrganisationEnvironment.value || isPartnerEnvironment.value) {
    return null;
  }
  const memberships = user.value.memberships || [];
  const current = memberships.find(
    (membership) => membership.organisation.id === user.value.org?.id,
  );
  if (!current?.via_partner_id) {
    return null;
  }
  return memberships.find(
    (membership) => membership.organisation.id === current.via_partner_id,
  ) || null;
});
// "Mijn merken", the partner's overview of its brands, always as the first item
// on top of the menu: in the partner environment and in a brand reached through
// the partner alike (review Ruben, #972). From a brand, opening it makes the
// partner active (PartnerHomeView).
const showMyBrandsNav = computed(() => (
  !isOrganisationMembershipUnverified.value
  && (isPartnerEnvironment.value || Boolean(brandPartnerMembership.value))
));
const homeRoute = computed(() => {
  if (isAdminEnvironment.value || !user.value.org) {
    return { name: 'admin-home' };
  }
  if (isPartnerEnvironment.value) {
    return { name: 'partner-home' };
  }
  return { name: 'organisation-home' };
});

const selectedOrganisation = ref(null);
const showAdminMoreMenu = ref(false);
const adminMoreMenuRef = ref(null);
const adminMoreToggleRef = ref(null);
const adminMoreMenuStyle = ref({});
const showMembershipMenu = ref(false);
const membershipMenuRef = ref(null);
const membershipToggleRef = ref(null);
const membershipMenuStyle = ref({});
const {
  hasOpenResearchRequests,
  fetchOpenResearchRequestCount: refreshOpenResearchRequestCount,
} = useOpenResearchRequests();
watch(selectedOrganisation, async (newValue, oldValue) => {
  if (!oldValue || !newValue || newValue === oldValue) {
    return;
  }
  const response = await organisationMembers.update(newValue, { primary: true });
  if (response.status !== 200) {
    notifyError('phrase.something_went_wrong');
  } else {
    document.location.href = router.resolve({ name: 'organisation-home' }).href;
  }
});

watch(showAdminEnvironment, () => {
  fetchOpenResearchRequestCount();
});

// "Mijn account" staat in de klant- en de reviewersomgeving direct onder het
// laatste item van het menu (Mijn bedrijf, Cadeaushop); elders onderaan, boven
// Uitloggen.
const NAV_ITEM_MY_ACCOUNT = {
  label: 'phrase.my_account',
  route: 'user-profile',
  imageLight: 'account/sm-light.svg',
  imageDark: 'account/sm-dark.svg',
};
const NAV_ITEMS_ORGANISATION = computed(() => [
  {
    label: 'word.dashboard',
    route: 'organisation-home',
    imageLight: 'home/sm-light.svg',
    imageDark: 'home/sm-dark.svg',
  },
  {
    label: 'phrase.request_research',
    route: 'request-research',
    imageLight: 'plus/sm-light.svg',
    imageDark: 'plus/sm-dark.svg',
  },
  {
    label: 'word.research_proposals',
    route: 'organisation-research-proposal-list',
    imageLight: 'documents/sm-light.svg',
    imageDark: 'documents/sm-dark.svg',
  },
  {
    label: 'word.ai_assistant',
    route: 'organisation-chat-thread-get-or-create',
    imageLight: 'ai-chat/sm-light.svg',
    imageDark: 'ai-chat/sm-dark.svg',
    locked: isAiInactive.value,
  },
  {
    label: 'word.documents',
    route: 'organisation-document-list',
    imageLight: 'documents/sm-light.svg',
    imageDark: 'documents/sm-dark.svg',
  },
  {
    label: 'phrase.my_organisation',
    route: 'organisation-detail',
    imageLight: 'organisation/sm-light.svg',
    imageDark: 'organisation/sm-dark.svg',
  },
  NAV_ITEM_MY_ACCOUNT,
]);
// The partner environment (ticket #972), below "Mijn merken" on top. There is no
// separate "Dashboard": the partner's landing is that same overview of its brands.
// AI assistant, documents and "Mijn bedrijf" are the organisation screens, for
// the partner itself.
const NAV_ITEMS_PARTNER = computed(() => [
  {
    label: 'phrase.request_research',
    route: 'partner-request-research',
    imageLight: 'plus/sm-light.svg',
    imageDark: 'plus/sm-dark.svg',
  },
  {
    label: 'word.researches',
    route: 'partner-researches',
    imageLight: 'researches/sm-light.svg',
    imageDark: 'researches/sm-dark.svg',
  },
  {
    label: 'word.ai_assistant',
    route: 'organisation-chat-thread-get-or-create',
    imageLight: 'ai-chat/sm-light.svg',
    imageDark: 'ai-chat/sm-dark.svg',
    locked: isAiInactive.value,
  },
  {
    label: 'word.documents',
    route: 'organisation-document-list',
    imageLight: 'documents/sm-light.svg',
    imageDark: 'documents/sm-dark.svg',
  },
  // "Mijn bedrijf", as in the client environment rather than the ticket's
  // "Bedrijfsprofiel": the page also holds the partner's colleagues, and the
  // company profile is one of its tabs (FID-6.1).
  {
    label: 'phrase.my_organisation',
    route: 'organisation-detail',
    imageLight: 'organisation/sm-light.svg',
    imageDark: 'organisation/sm-dark.svg',
  },
]);
const NAV_ITEMS_ORGANISATION_VISIBLE = computed(() => {
  if (isOrganisationMembershipUnverified.value) {
    return [];
  }
  return isPartnerEnvironment.value ? NAV_ITEMS_PARTNER.value : NAV_ITEMS_ORGANISATION.value;
});


const NAV_ITEMS_ADMIN = computed(() => [
  {
    label: 'word.dashboard',
    route: 'admin-home',
    imageLight: 'home/sm-light.svg',
    imageDark: 'home/sm-dark.svg',
  },
  {
    label: 'word.researches',
    route: 'admin-research-list',
    imageLight: 'researches/sm-light.svg',
    imageDark: 'researches/sm-dark.svg',
  },
  {
    label: 'word.research_proposals',
    route: 'admin-research-proposal-list',
    imageLight: 'documents/sm-light.svg',
    imageDark: 'documents/sm-dark.svg',
    showDot: hasOpenResearchRequests.value,
  },
  {
    label: 'word.research_sets_plural',
    route: 'admin-research-set-list',
    imageLight: 'researches/sm-light.svg',
    imageDark: 'researches/sm-dark.svg',
  },
  {
    label: 'word.organisations',
    route: 'admin-organisation-list',
    imageLight: 'organisations/sm-light.svg',
    imageDark: 'organisations/sm-dark.svg',
  },
  {
    label: 'word.review_sessions',
    route: 'admin-review-session-list',
    imageLight: 'table/sm-light.svg',
    imageDark: 'table/sm-dark.svg',
  },
  {
    label: 'word.items',
    route: 'admin-item-list',
    imageLight: 'questionnaire/sm-light.svg',
    imageDark: 'questionnaire/sm-dark.svg',
  },
  {
    label: 'word.documents',
    route: 'admin-organisation-document-list',
    imageLight: 'documents/sm-light.svg',
    imageDark: 'documents/sm-dark.svg',
  },
  {
    label: 'word.ai',
    route: 'admin-chat-thread-list',
    imageLight: 'robot/sm-light.svg',
    imageDark: 'robot/sm-dark.svg',
  },
  {
    label: 'word.countries',
    route: 'admin-country-list',
    imageLight: 'globe/sm-light.svg',
    imageDark: 'globe/sm-dark.svg',
  },
  {
    label: 'phrase.api_documentation',
    route: 'admin-api-docs',
    imageLight: 'plug/sm-light.svg',
    imageDark: 'plug/sm-dark.svg',
  },
])

const NAV_ITEMS_ADMIN_EXTRA = [
  {
    label: 'word.newsletter',
    route: 'admin-newsletter-registration-list',
    imageLight: 'newspaper/sm-light.svg',
    imageDark: 'newspaper/sm-dark.svg',
  },
  {
    label: 'word.logins',
    route: 'admin-user-event-list',
    imageLight: 'sign-in/sm-light.svg',
    imageDark: 'sign-in/sm-dark.svg',
  },
  {
    label: 'word.styleguide',
    route: 'admin-icon-list',
    imageLight: 'table/sm-light.svg',
    imageDark: 'table/sm-dark.svg',
  },
]

const NAV_ITEMS_USER_AUTHENTICATED = [
  NAV_ITEM_MY_ACCOUNT,
  {
    label: 'word.logout',
    href: '/ui/v2/logout',
    imageLight: 'sign-out/sm-light.svg',
    imageDark: 'sign-out/sm-dark.svg',
    primaryLight: true,
  },
];
// Alleen in de reviewersomgeving, de enige plek waar de sidebar uitgelogd staat.
const NAV_ITEMS_USER_ANONYMOUS = [
  {
    label: 'word.login',
    href: '/ui/v2/login',
    imageLight: 'sign-in/sm-light.svg',
    imageDark: 'sign-in/sm-dark.svg',
    primaryLight: true,
  },
  {
    label: 'word.register',
    href: '/ui/v2/register-account',
    imageLight: 'account/sm-light.svg',
    imageDark: 'account/sm-dark.svg',
  },
];
const NAV_ITEMS_USER = computed(() => {
  if (!user.value.isAuthenticated) {
    return withReviewerIcons(NAV_ITEMS_USER_ANONYMOUS);
  }
  // Staat "Mijn account" al in het menu erboven, dan hier alleen Uitloggen. Een
  // lid dat nog niet geverifieerd is ziet het menu van het bedrijf niet, en
  // houdt hem dus hier.
  const accountInMenu = isReviewerEnvironment.value || (
    showOrganisationEnvironment.value
    && !isPartnerEnvironment.value
    && !isOrganisationMembershipUnverified.value
  );
  const items = accountInMenu
    ? NAV_ITEMS_USER_AUTHENTICATED.filter((item) => item !== NAV_ITEM_MY_ACCOUNT)
    : NAV_ITEMS_USER_AUTHENTICATED;
  return withReviewerIcons(items);
});

// Het saldo van de reviewer, als voortgangsbalk onder het logo. Alleen bij een
// actief account; is het nog niet actief, dan vraagt "Mijn coins" zelf om het
// te activeren.
const coinRate = computed(() => {
  const coinAccount = user.value.coin_account;
  if (!isReviewerEnvironment.value || !user.value.isAuthenticated || !coinAccount?.is_active) {
    return null;
  }
  return coinAccount.rate || 0;
});

// Bovenaan in de reviewersomgeving. Alle commercials en de Cadeaushop zijn ook
// uitgelogd te bekijken.
const NAV_ITEMS_REVIEWER = computed(() => {
  const items = [{
    label: 'word.dashboard',
    route: 'public-dashboard',
    imageLight: 'home/sm-dark.svg',
    imageDark: 'home/sm-dark.svg',
  }, {
    label: 'phrase.all_commercials',
    route: 'public-commercial-list',
    imageLight: 'tv/sm-dark.svg',
    imageDark: 'tv/sm-dark.svg',
  }];
  if (user.value.isAuthenticated) {
    items.push({
      label: 'phrase.my_coins',
      route: 'reviewer-dashboard',
      imageLight: 'coins/sm-light.svg',
      imageDark: 'coins/sm-dark.svg',
    });
  }
  items.push({
    label: 'word.marketplace',
    route: 'reviewer-product-list',
    imageLight: 'gift-shop/sm-light.svg',
    imageDark: 'gift-shop/sm-dark.svg',
  });
  if (user.value.isAuthenticated) {
    // Met de lichte sidebar van de reviewer: via `user-profile` kreeg iemand
    // met een membership de donkere sidebar van het bedrijf.
    items.push({ ...NAV_ITEM_MY_ACCOUNT, route: 'reviewer-user-profile' });
  }
  return withReviewerIcons(items);
});

// Onderaan in de reviewersomgeving, boven Mijn account: de bedrijfsomgeving op
// naam van het bedrijf bij een membership, en de adminomgeving voor staff.
const NAV_ITEMS_REVIEWER_ENVIRONMENTS = computed(() => {
  if (!user.value.isAuthenticated) {
    return [];
  }
  const items = [];
  if (hasOrganisationMembership.value) {
    const organisation = user.value.org || user.value.memberships?.[0]?.organisation;
    items.push({
      // De naam van het bedrijf; "Bedrijfsomgeving" alleen als die ontbreekt.
      label: organisation?.name ? null : 'word.organisation_environment',
      rawLabel: organisation?.name,
      route: isPartnerOrganisation(organisation) ? 'partner-home' : 'organisation-home',
      imageLight: 'organisation/sm-light.svg',
      imageDark: 'organisation/sm-dark.svg',
    });
  }
  if (user.value.sust) {
    items.push({
      label: 'word.admin_environment',
      route: 'admin-home',
      imageLight: 'admin-environment/sm-light.svg',
      imageDark: 'admin-environment/sm-dark.svg',
    });
  }
  return withReviewerIcons(items);
});

const environmentNavItem = computed(() => {
  if (isAdminEnvironment.value) {
    return {
      label: 'word.organisation_environment',
      route: 'organisation-home',
      imageLight: 'organisation/sm-light.svg',
      imageDark: 'organisation/sm-dark.svg',
    };
  }
  return {
    label: 'word.admin_environment',
    route: 'admin-home',
    imageLight: 'admin-environment/sm-light.svg',
    imageDark: 'admin-environment/sm-dark.svg',
  };
});

const fetchOpenResearchRequestCount = async () => {
  if (!showAdminEnvironment.value) {
    hasOpenResearchRequests.value = false;
    return;
  }
  await refreshOpenResearchRequestCount();
};

const isAdminMoreRouteActive = computed(() => false);

watch(
  () => route.fullPath,
  () => {
    showAdminMoreMenu.value = false;
  },
);

const toggleAdminMoreMenu = () => {
  showAdminMoreMenu.value = !showAdminMoreMenu.value;
  if (showAdminMoreMenu.value) {
    nextTick(() => {
      updateAdminMoreMenuPosition();
    });
  }
};

const selectAdminMoreItem = (routeName) => {
  showAdminMoreMenu.value = false;
  router.push({ name: routeName });
};

const toggleSideNavControl = () => {
  if (isMobileViewport.value) {
    store.commit('toggleSideNav');
    return;
  }
  store.commit('toggleSideNavCollapse');
};

const toggleMembershipMenu = () => {
  showMembershipMenu.value = !showMembershipMenu.value;
  if (showMembershipMenu.value) {
    nextTick(() => {
      updateMembershipMenuPosition();
    });
  }
};

const selectMembership = (membershipId) => {
  showMembershipMenu.value = false;
  selectedOrganisation.value = membershipId;
};

const onResize = () => {
  windowWidth.value = window.innerWidth;
  if (showMembershipMenu.value) {
    updateMembershipMenuPosition();
  }
  if (showAdminMoreMenu.value) {
    updateAdminMoreMenuPosition();
  }
};

const onDocumentClick = (event) => {
  if (showMembershipMenu.value) {
    if (membershipMenuRef.value?.contains(event.target)) {
      return;
    }
    showMembershipMenu.value = false;
  }

  if (showAdminMoreMenu.value) {
    if (adminMoreMenuRef.value?.contains(event.target)) {
      return;
    }
    if (adminMoreToggleRef.value?.contains(event.target)) {
      return;
    }
    showAdminMoreMenu.value = false;
  }
};

const updateMembershipMenuPosition = () => {
  if (!membershipToggleRef.value || !showMembershipMenu.value) {
    return;
  }
  const rect = membershipToggleRef.value.getBoundingClientRect();
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const menuWidth = 260;
  const left = Math.min(rect.right + 8, viewportWidth - menuWidth - 8);
  const top = Math.max(8, rect.top - 2);
  const bottomGap = 16;
  const maxHeight = Math.max(120, viewportHeight - top - bottomGap);
  membershipMenuStyle.value = {
    position: 'fixed',
    top: `${top}px`,
    left: `${Math.max(8, left)}px`,
    minWidth: `${menuWidth}px`,
    maxWidth: '320px',
    maxHeight: `${maxHeight}px`,
    overflowY: 'auto',
    zIndex: 1300,
  };
};

const updateAdminMoreMenuPosition = () => {
  if (!adminMoreToggleRef.value || !showAdminMoreMenu.value) {
    return;
  }
  const rect = adminMoreToggleRef.value.getBoundingClientRect();
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const menuWidth = 260;
  const left = Math.min(rect.right + 8, viewportWidth - menuWidth - 8);
  const top = Math.max(8, rect.top - 2);
  const bottomGap = 16;
  const maxHeight = Math.max(120, viewportHeight - top - bottomGap);
  adminMoreMenuStyle.value = {
    position: 'fixed',
    top: `${top}px`,
    left: `${Math.max(8, left)}px`,
    minWidth: `${menuWidth}px`,
    maxWidth: '320px',
    maxHeight: `${maxHeight}px`,
    overflowY: 'auto',
    zIndex: 1300,
  };
};

onMounted(() => {
  onResize();
  window.addEventListener('resize', onResize, { passive: true });
  if (user.value.org) {
    selectedOrganisation.value = user.value.org.membership.id;
  }
  fetchOpenResearchRequestCount();
  document.addEventListener('click', onDocumentClick);
  window.addEventListener('scroll', updateMembershipMenuPosition, true);
  window.addEventListener('scroll', updateAdminMoreMenuPosition, true);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize);
  document.removeEventListener('click', onDocumentClick);
  window.removeEventListener('scroll', updateMembershipMenuPosition, true);
  window.removeEventListener('scroll', updateAdminMoreMenuPosition, true);
});
</script>

<template>
  <aside
    v-if="isDesktopViewport"
    class="sidenav sidenav-desktop navbar navbar-vertical navbar-expand-xs fixed-start"
    :class="[sideNavClass, { 'is-collapsed': isCollapsedDesktop }]"
    id="sidenav-main"
    data-color="warning"
  >
    <div
      class="sidenav-header d-flex justify-content-center align-items-center"
    >
      <router-link
        :to="logoRoute"
        class="org-header-logo-frame p-3 m-0"
      >
        <img
          v-if="user.org?.logo && route.meta.app === 'organisation'"
          :src="user.org.logo"
          :alt="user.org.name"
          class="img-fluid border-radius-lg"
        />
        <span
          v-else-if="route.meta.app === 'organisation' && organisationInitials"
          class="org-initials-logo border-radius-lg"
          :title="user.org.name"
          data-testid="organisation-initials-logo"
        >{{ organisationInitials }}</span>
        <img
          v-else-if="route.meta.app === 'organisation'"
          :src="stramigoDefaultLogo"
          class="img-fluid border-radius-lg stramigo-face-logo"
          :alt="sideNavLogoAlt"
        />
        <img
          v-else-if="isAdminEnvironment"
          :src="adminEnvironmentHeaderLogo"
          :alt="t('word.admin_environment')"
          class="img-fluid border-radius-lg admin-header-logo"
        />
        <img
          v-else
          :src="sideNavLogo"
          class="img-fluid border-radius-lg"
          :alt="sideNavLogoAlt"
        />
      </router-link>
      <div
        v-if="showOrganisationEnvironment"
        ref="membershipMenuRef"
        class="org-header-row"
        :class="{ 'is-collapsed': isCollapsedDesktop }"
      >
        <div
          v-if="!isCollapsedDesktop"
          class="org-header-name"
        >
          {{ currentOrganisationName }}
        </div>
        <button
          v-if="hasMultipleMemberships"
          ref="membershipToggleRef"
          type="button"
          class="btn btn-sm btn-secondary org-switcher-toggle m-0"
          @click.stop="toggleMembershipMenu"
        >
          <i class="fa fa-exchange-alt" />
        </button>
        <transition name="org-menu">
          <div
            v-if="showMembershipMenu"
            class="org-switcher-menu"
            :style="membershipMenuStyle"
          >
            <template
              v-for="{ membership, dividerAfter } in switcherMemberships"
              :key="membership.id"
            >
              <button
                type="button"
                class="btn btn-link org-switcher-item text-start"
                :class="{ 'is-active': selectedOrganisation === membership.id }"
                @click="selectMembership(membership.id)"
              >
                <img
                  v-if="membership.organisation.logo"
                  :src="membership.organisation.logo"
                  :alt="membership.organisation.name"
                  class="org-switcher-logo me-2"
                />
                <i v-else class="fa fa-building me-2" />
                {{ membership.organisation.name }}
              </button>
              <div
                v-if="dividerAfter"
                class="org-switcher-divider"
                data-testid="org-switcher-partner-divider"
              />
            </template>
          </div>
        </transition>
      </div>
      <hr
        v-if="showOrganisationEnvironment"
        class="org-header-separator"
      >
    </div>
    <coin-progress-bar
      v-if="coinRate !== null"
      :rate="coinRate"
      :collapsed="isCollapsedDesktop"
    />
    <hr class="horizontal dark mt-0 mb-2"/>

    <div
      class="sidenav-scroll-area"
      id="sidenav-collapse-main"
    >
      <template v-if="isReviewerEnvironment">
        <ul class="navbar-nav overflow-x-hidden">
          <side-nav-bar-item
            v-for="navBarItem in NAV_ITEMS_REVIEWER"
            :key="`${navBarItem.route || navBarItem.href}`"
            :label="navBarItem.label"
            :icon="navBarItem.icon"
            :image-light="navBarItem.imageLight"
            :image-dark="navBarItem.imageDark"
            :route="navBarItem.route"
            :href="navBarItem.href"
            :badge="navBarItem.badge"
            :collapsed="isCollapsedDesktop"
            :active="route.meta.navRouteActive ? route.meta.navRouteActive === navBarItem.route : false"
          />
        </ul>
        <hr class="horizontal dark" />
      </template>
      <template v-if="showMyBrandsNav">
        <ul class="navbar-nav overflow-x-hidden brand-partner-nav">
          <side-nav-bar-item
            label="phrase.my_brands"
            image-light="organisations/sm-light.svg"
            image-dark="organisations/sm-dark.svg"
            route="partner-brands"
            :collapsed="isCollapsedDesktop"
            :active="route.meta.navRouteActive === 'partner-brands'"
            data-testid="brand-partner-nav-item"
          />
        </ul>
        <hr class="horizontal dark my-1" />
      </template>
      <ul
        class="navbar-nav overflow-x-hidden"
        v-if="showOrganisationEnvironment"
      >
        <side-nav-bar-item
          v-for="navBarItem in NAV_ITEMS_ORGANISATION_VISIBLE"
          :key="navBarItem.name"
          :label="navBarItem.label"
          :icon="navBarItem.icon"
          :image-light="navBarItem.imageLight"
          :image-dark="navBarItem.imageDark"
          :media-image="navBarItem.mediaImage"
          :route="navBarItem.route"
          :route-to="navBarItem.routeTo"
          :href="navBarItem.href"
          :badge="navBarItem.badge"
          :locked="navBarItem.locked"
          :sub-item="navBarItem.subItem && !isCollapsedDesktop"
          :collapsed="isCollapsedDesktop"
          :active="route.meta.navRouteActive ? route.meta.navRouteActive === navBarItem.route : false"
        />
      </ul>
      <hr
        v-if="showOrganisationEnvironment && !isOrganisationMembershipUnverified"
        class="horizontal dark"
      />
      <ul
        v-if="showAdminEnvironment"
        class="navbar-nav overflow-x-hidden"
      >
        <li
          v-if="!isCollapsedDesktop"
          class="nav-item mt-3"
        >
          <h6
            class="text-truncate ps-3 ms-2 text-uppercase text-xs font-weight-bolder text-white"
          >
            ADMIN {{ app?.version || '' }}
          </h6>
        </li>

        <side-nav-bar-item
          v-for="navBarItem in NAV_ITEMS_ADMIN"
          :key="navBarItem.name"
          :nav-bar-item="navBarItem"
          :label="navBarItem.label"
          :raw-label="navBarItem.rawLabel"
          :icon="navBarItem.icon"
          :image-light="navBarItem.imageLight"
          :image-dark="navBarItem.imageDark"
          :media-image="navBarItem.mediaImage"
          :route="navBarItem.route"
          :href="navBarItem.href"
          :target="navBarItem.target"
          :badge="navBarItem.badge"
          :show-dot="navBarItem.showDot"
          :sub-item="navBarItem.subItem && !isCollapsedDesktop"
          :collapsed="isCollapsedDesktop"
          :active="route.meta.navRouteActive ? route.meta.navRouteActive === navBarItem.route : false"
        />
        <side-nav-bar-item
          label="word.django_admin"
          href="/admin/"
          image-light="database/sm-light.svg"
          image-dark="database/sm-dark.svg"
          :collapsed="isCollapsedDesktop"
        />
        <li class="nav-item">
          <a
            ref="adminMoreToggleRef"
            href="#"
            class="nav-link admin-more-trigger"
            :class="{ active: isAdminMoreRouteActive, 'is-collapsed': isCollapsedDesktop }"
            @click.prevent="toggleAdminMoreMenu"
          >
            <span class="nav-icon-wrapper text-center">
              <span class="admin-more-chevron" :class="{ 'is-open': showAdminMoreMenu }">
                <nav-icon
                  image-light="chevron-down/sm-light.svg"
                  image-dark="chevron-down/sm-dark.svg"
                  :active="isAdminMoreRouteActive"
                />
              </span>
            </span>
            <span
              v-if="!isCollapsedDesktop"
              class="nav-link-text truncate-text ms-1 ps-1"
            >{{ $t('word.more') }}</span>
          </a>
        </li>
        <transition name="org-menu">
          <div
            v-if="showAdminMoreMenu"
            ref="adminMoreMenuRef"
            class="org-switcher-menu admin-more-menu"
            :style="adminMoreMenuStyle"
          >
            <button
              v-for="navBarItem in NAV_ITEMS_ADMIN_EXTRA"
              :key="`more-${navBarItem.route}`"
              type="button"
              class="btn btn-link org-switcher-item text-start d-flex align-items-center"
              @click="selectAdminMoreItem(navBarItem.route)"
            >
              <nav-icon
                :image-light="navBarItem.imageLight"
                :image-dark="navBarItem.imageDark"
                  :active="false"
                />
              <span class="ms-2">{{ $t(navBarItem.label) }}</span>
            </button>
          </div>
        </transition>
      </ul>
      <hr
        v-if="showAdminEnvironment"
        class="horizontal dark"
      />
      <ul class="navbar-nav overflow-x-hidden">
        <side-nav-bar-item
          v-if="showEnvironmentSwitcher"
          :label="environmentNavItem.label"
          :icon="environmentNavItem.icon"
          :image-light="environmentNavItem.imageLight"
          :image-dark="environmentNavItem.imageDark"
          :route="environmentNavItem.route"
          :collapsed="isCollapsedDesktop"
          :active="route.meta.navRouteActive ? route.meta.navRouteActive === environmentNavItem.route : false"
        />
        <side-nav-bar-item
          v-if="!isPartnerEnvironment && !isReviewerEnvironment"
          label="word.reviewers_dashboard"
          image-light="reviewers-dashboard/sm-light.svg"
          image-dark="reviewers-dashboard/sm-dark.svg"
          route="public-dashboard"
          :collapsed="isCollapsedDesktop"
        />
        <template v-if="isReviewerEnvironment">
          <side-nav-bar-item
            v-for="navBarItem in NAV_ITEMS_REVIEWER_ENVIRONMENTS"
            :key="`env-${navBarItem.route}`"
            :label="navBarItem.label"
            :raw-label="navBarItem.rawLabel"
            :image-light="navBarItem.imageLight"
            :image-dark="navBarItem.imageDark"
            :route="navBarItem.route"
            :collapsed="isCollapsedDesktop"
          />
        </template>

        <side-nav-bar-item
          v-for="navBarItem in NAV_ITEMS_USER"
          :key="navBarItem.name"
          :nav-bar-item="navBarItem"
          :label="navBarItem.label"
          :icon="navBarItem.icon"
          :image-light="navBarItem.imageLight"
          :image-dark="navBarItem.imageDark"
          :media-image="navBarItem.mediaImage"
          :route="navBarItem.route"
          :href="navBarItem.href"
          :target="navBarItem.target"
          :badge="navBarItem.badge"
          :danger="navBarItem.danger"
          :primary-light="navBarItem.primaryLight"
          :collapsed="isCollapsedDesktop"
          :active="route.meta.navRouteActive ? route.meta.navRouteActive === navBarItem.route : false"
        />
      </ul>
    </div>

    <div class="sidenav-toggle-footer">
      <button
        type="button"
        class="btn btn-sm btn-light d-flex align-items-center justify-content-center"
        @click="toggleSideNavControl"
      >
        <i class="fa" :class="`fa-${sideNavToggleIcon}`" />
      </button>
    </div>
  </aside>

  <button
    v-if="isMobileViewport"
    type="button"
    class="mobile-sidenav-toggle btn btn-primary"
    :class="{ 'is-shifted': showSideNav }"
    @click="toggleSideNavControl"
  >
    <i
      class="fa"
      :class="`fa-${sideNavToggleIcon}`"
    />
  </button>

  <transition name="mobile-sidenav">
    <aside
      v-if="isMobileViewport && showSideNav"
      class="sidenav sidenav-mobile navbar navbar-vertical navbar-expand-xs fixed-start"
      :class="[sideNavClass]"
      data-color="warning"
    >
    <div
      class="sidenav-header d-flex justify-content-center align-items-center"
    >
      <router-link
        :to="logoRoute"
        class="org-header-logo-frame p-3 m-0"
      >
        <img
          v-if="user.org?.logo && route.meta.app === 'organisation'"
          :src="user.org.logo"
          :alt="user.org.name"
          class="img-fluid border-radius-lg"
        />
        <span
          v-else-if="route.meta.app === 'organisation' && organisationInitials"
          class="org-initials-logo border-radius-lg"
          :title="user.org.name"
          data-testid="organisation-initials-logo"
        >{{ organisationInitials }}</span>
        <img
          v-else-if="route.meta.app === 'organisation'"
          :src="stramigoDefaultLogo"
          class="img-fluid border-radius-lg stramigo-face-logo"
          :alt="sideNavLogoAlt"
        />
        <img
          v-else-if="isAdminEnvironment"
          :src="adminEnvironmentHeaderLogo"
          :alt="t('word.admin_environment')"
          class="img-fluid border-radius-lg admin-header-logo"
        />
        <img
          v-else
          :src="sideNavLogo"
          class="img-fluid border-radius-lg"
          :alt="sideNavLogoAlt"
        />
      </router-link>
      <div
        v-if="showOrganisationEnvironment"
        ref="membershipMenuRef"
        class="org-header-row"
      >
        <div class="org-header-name">
          {{ currentOrganisationName }}
        </div>
        <button
          v-if="hasMultipleMemberships"
          ref="membershipToggleRef"
          type="button"
          class="btn btn-sm btn-secondary org-switcher-toggle m-0"
          @click.stop="toggleMembershipMenu"
        >
          <i class="fa fa-exchange-alt" />
        </button>
        <transition name="org-menu">
          <div
            v-if="showMembershipMenu"
            class="org-switcher-menu"
            :style="membershipMenuStyle"
          >
            <template
              v-for="{ membership, dividerAfter } in switcherMemberships"
              :key="membership.id"
            >
              <button
                type="button"
                class="btn btn-link org-switcher-item text-start"
                :class="{ 'is-active': selectedOrganisation === membership.id }"
                @click="selectMembership(membership.id)"
              >
                <img
                  v-if="membership.organisation.logo"
                  :src="membership.organisation.logo"
                  :alt="membership.organisation.name"
                  class="org-switcher-logo me-2"
                />
                <i v-else class="fa fa-building me-2" />
                {{ membership.organisation.name }}
              </button>
              <div
                v-if="dividerAfter"
                class="org-switcher-divider"
                data-testid="org-switcher-partner-divider"
              />
            </template>
          </div>
        </transition>
      </div>
      <hr
        v-if="showOrganisationEnvironment"
        class="org-header-separator"
      >
    </div>
    <coin-progress-bar
      v-if="coinRate !== null"
      :rate="coinRate"
    />
    <hr class="horizontal dark mt-0 mb-2"/>

    <div class="sidenav-scroll-area">
      <template v-if="isReviewerEnvironment">
        <ul class="navbar-nav overflow-x-hidden">
          <side-nav-bar-item
            v-for="navBarItem in NAV_ITEMS_REVIEWER"
            :key="`m-${navBarItem.route || navBarItem.href}`"
            :label="navBarItem.label"
            :icon="navBarItem.icon"
            :image-light="navBarItem.imageLight"
            :image-dark="navBarItem.imageDark"
            :route="navBarItem.route"
            :href="navBarItem.href"
            :badge="navBarItem.badge"
            :collapsed="false"
            :active="route.meta.navRouteActive ? route.meta.navRouteActive === navBarItem.route : false"
          />
        </ul>
        <hr class="horizontal dark" />
      </template>
      <template v-if="showMyBrandsNav">
        <ul class="navbar-nav overflow-x-hidden brand-partner-nav">
          <side-nav-bar-item
            label="phrase.my_brands"
            image-light="organisations/sm-light.svg"
            image-dark="organisations/sm-dark.svg"
            route="partner-brands"
            :collapsed="false"
            :active="route.meta.navRouteActive === 'partner-brands'"
            data-testid="brand-partner-nav-item"
          />
        </ul>
        <hr class="horizontal dark my-1" />
      </template>
      <ul
        class="navbar-nav overflow-x-hidden"
        v-if="showOrganisationEnvironment"
      >
        <side-nav-bar-item
          v-for="navBarItem in NAV_ITEMS_ORGANISATION_VISIBLE"
          :key="`m-${navBarItem.name}`"
          :label="navBarItem.label"
          :icon="navBarItem.icon"
          :image-light="navBarItem.imageLight"
          :image-dark="navBarItem.imageDark"
          :media-image="navBarItem.mediaImage"
          :route="navBarItem.route"
          :route-to="navBarItem.routeTo"
          :href="navBarItem.href"
          :badge="navBarItem.badge"
          :locked="navBarItem.locked"
          :sub-item="navBarItem.subItem"
          :collapsed="false"
          :active="route.meta.navRouteActive ? route.meta.navRouteActive === navBarItem.route : false"
        />
      </ul>
      <hr v-if="showOrganisationEnvironment && !isOrganisationMembershipUnverified" class="horizontal dark" />
      <ul v-if="showAdminEnvironment" class="navbar-nav overflow-x-hidden">
        <li class="nav-item mt-3">
          <h6 class="text-truncate ps-3 ms-2 text-uppercase text-xs font-weight-bolder text-white">
            ADMIN {{ app?.version || '' }}
          </h6>
        </li>
        <side-nav-bar-item
          v-for="navBarItem in NAV_ITEMS_ADMIN"
          :key="`m-${navBarItem.name}`"
          :label="navBarItem.label"
          :raw-label="navBarItem.rawLabel"
          :icon="navBarItem.icon"
          :image-light="navBarItem.imageLight"
          :image-dark="navBarItem.imageDark"
          :media-image="navBarItem.mediaImage"
          :route="navBarItem.route"
          :href="navBarItem.href"
          :target="navBarItem.target"
          :badge="navBarItem.badge"
          :show-dot="navBarItem.showDot"
          :sub-item="navBarItem.subItem"
          :collapsed="false"
          :active="route.meta.navRouteActive ? route.meta.navRouteActive === navBarItem.route : false"
        />
        <side-nav-bar-item
          label="word.django_admin"
          href="/admin/"
          image-light="database/sm-light.svg"
          image-dark="database/sm-dark.svg"
          :collapsed="false"
        />
        <li class="nav-item">
          <a
            ref="adminMoreToggleRef"
            href="#"
            class="nav-link admin-more-trigger"
            :class="{ active: isAdminMoreRouteActive }"
            @click.prevent="toggleAdminMoreMenu"
          >
            <span class="nav-icon-wrapper text-center">
              <span class="admin-more-chevron" :class="{ 'is-open': showAdminMoreMenu }">
                <nav-icon
                  image-light="chevron-down/sm-light.svg"
                  image-dark="chevron-down/sm-dark.svg"
                  :active="isAdminMoreRouteActive"
                />
              </span>
            </span>
            <span class="nav-link-text truncate-text ms-1 ps-1">{{ $t('word.more') }}</span>
          </a>
        </li>
        <transition name="org-menu">
          <div
            v-if="showAdminMoreMenu"
            ref="adminMoreMenuRef"
            class="org-switcher-menu admin-more-menu"
            :style="adminMoreMenuStyle"
          >
            <button
              v-for="navBarItem in NAV_ITEMS_ADMIN_EXTRA"
              :key="`m-more-${navBarItem.route}`"
              type="button"
              class="btn btn-link org-switcher-item text-start d-flex align-items-center"
              @click="selectAdminMoreItem(navBarItem.route)"
            >
              <nav-icon
                :image-light="navBarItem.imageLight"
                :image-dark="navBarItem.imageDark"
                  :active="false"
                />
              <span class="ms-2">{{ $t(navBarItem.label) }}</span>
            </button>
          </div>
        </transition>
      </ul>
      <hr v-if="showAdminEnvironment" class="horizontal dark" />
      <ul class="navbar-nav overflow-x-hidden">
        <side-nav-bar-item
          v-if="showEnvironmentSwitcher"
          :label="environmentNavItem.label"
          :icon="environmentNavItem.icon"
          :image-light="environmentNavItem.imageLight"
          :image-dark="environmentNavItem.imageDark"
          :route="environmentNavItem.route"
          :collapsed="false"
          :active="route.meta.navRouteActive ? route.meta.navRouteActive === environmentNavItem.route : false"
        />
        <side-nav-bar-item
          v-if="!isPartnerEnvironment && !isReviewerEnvironment"
          label="word.reviewers_dashboard"
          image-light="reviewers-dashboard/sm-light.svg"
          image-dark="reviewers-dashboard/sm-dark.svg"
          route="public-dashboard"
          :collapsed="false"
        />
        <template v-if="isReviewerEnvironment">
          <side-nav-bar-item
            v-for="navBarItem in NAV_ITEMS_REVIEWER_ENVIRONMENTS"
            :key="`env-${navBarItem.route}`"
            :label="navBarItem.label"
            :raw-label="navBarItem.rawLabel"
            :image-light="navBarItem.imageLight"
            :image-dark="navBarItem.imageDark"
            :route="navBarItem.route"
            :collapsed="false"
          />
        </template>
        <side-nav-bar-item
          v-for="navBarItem in NAV_ITEMS_USER"
          :key="`m-${navBarItem.name}`"
          :label="navBarItem.label"
          :icon="navBarItem.icon"
          :image-light="navBarItem.imageLight"
          :image-dark="navBarItem.imageDark"
          :media-image="navBarItem.mediaImage"
          :route="navBarItem.route"
          :href="navBarItem.href"
          :target="navBarItem.target"
          :badge="navBarItem.badge"
          :danger="navBarItem.danger"
          :primary-light="navBarItem.primaryLight"
          :collapsed="false"
          :active="route.meta.navRouteActive ? route.meta.navRouteActive === navBarItem.route : false"
        />
      </ul>
    </div>
    </aside>
  </transition>

</template>

<style scoped>
aside.sidenav {
  display: flex !important;
  flex-direction: column !important;
  flex-wrap: nowrap !important;
  align-items: stretch !important;
  height: 100vh;
  overflow: hidden;
  width: 15rem;
  min-width: 15rem;
  max-width: 15rem;
  transition: width 0.2s ease-in-out;
}

aside.sidenav-visible {
  transform: translateX(0) !important;
}

aside.sidenav-default {
  background-color: rgba(52, 58, 64, 0.95);
}

aside.sidenav-admin {
  background-color: rgba(23, 35, 59, 0.97);
}

/* De reviewersomgeving: licht, met donkere tekst en iconen. */
aside.sidenav-reviewer {
  background-color: rgba(250, 250, 252, 0.98);
  border-right: 1px solid rgba(20, 33, 56, 0.1);
}

aside.sidenav-reviewer :deep(.nav-link),
aside.sidenav-reviewer :deep(.nav-link-text),
aside.sidenav-reviewer :deep(.nav-icon) {
  color: #1f2a44 !important;
}

aside.sidenav-reviewer :deep(.nav-link.nav-link-primary-light),
aside.sidenav-reviewer :deep(.nav-link.nav-link-primary-light .nav-link-text),
aside.sidenav-reviewer :deep(.nav-link.nav-link-primary-light .nav-icon) {
  color: var(--primary-color-dark) !important;
}

aside.sidenav-reviewer :deep(.nav-link:not(.active):hover) {
  background-color: rgba(20, 33, 56, 0.06);
}

aside.sidenav-admin .sidenav-header {
  background-color: hsla(0, 0%, 78%, 0.2);
}

.nav-icon {
  width: 25px;
}

.nav-icon-wrapper {
  width: 27px;
}

.sidenav-desktop.is-collapsed {
  width: 4.25rem;
  min-width: 4.25rem;
  max-width: 4.25rem;
}

.sidenav-header {
  flex: 0 0 auto;
  width: 100%;
  overflow: hidden;
  flex-direction: column;
}

.sidenav-header .img-fluid {
  max-width: 100%;
  height: auto;
  max-height: 148px;
  object-fit: contain;
}

.org-header-logo-frame {
  width: min(100%, 11.5rem);
  aspect-ratio: 1 / 1;
  min-height: clamp(3rem, 20vh, 240px);
}

.sidenav-header .admin-header-logo {
  max-height: 140px;
  width: 180px;
  max-width: 92%;
}

.sidenav-header .stramigo-face-logo {
  max-width: 90%;
}

/* "Alle merken" on top of a brand's menu, a little more compact than the menu
   items below it, so the extra line does not make the side nav scroll on a
   900px high screen. Specific enough to beat the theme's
   `.navbar-vertical.navbar-expand-xs .navbar-nav .nav-link` padding. */
.sidenav.navbar-vertical .brand-partner-nav :deep(.nav-link) {
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}

.org-initials-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 70%;
  aspect-ratio: 1 / 1;
  background: var(--primary-color);
  color: #fff;
  font-size: 1.9rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.sidenav-desktop.is-collapsed .org-initials-logo {
  width: 42px;
  font-size: 0.9rem;
}

.sidenav-desktop:not(.is-collapsed) .sidenav-header .stramigo-face-logo {
  max-width: 50%;
}

.sidenav-header a {
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

.org-header-row {
  width: 100%;
  margin-top: 0.1rem;
  padding: 0 0.85rem 0.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.45rem;
}

.org-header-name {
  flex: 1 1 auto;
  min-width: 0;
  max-width: calc(100% - 36px);
  color: #fff;
  font-size: 0.78rem;
  font-weight: 700;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.org-header-row.is-collapsed {
  justify-content: center;
}

.org-header-separator {
  width: calc(100% - 1.4rem);
  border: 0;
  border-top: 1px solid #f78e47;
  margin: 0.2rem auto 0.4rem;
}

.sidenav-scroll-area {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  display: block !important;
  overflow-y: auto !important;
  overflow-x: hidden !important;
  padding-bottom: 4rem;
}

.sidenav-scroll-area .navbar-nav {
  width: 100%;
  overflow-x: hidden !important;
}

.admin-more-chevron {
  transition: transform 0.2s ease;
  display: inline-flex;
  align-items: center;
}

.admin-more-chevron.is-open {
  transform: rotate(-90deg);
}

.admin-more-trigger {
  display: flex;
  align-items: center;
  min-height: 2.65rem;
}

.sidenav-toggle-footer {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 0.6rem;
  border-top: 1px solid rgba(255, 255, 255, 0.16);
  background: inherit;
  z-index: 3;
}

.sidenav-toggle-footer .btn {
  min-height: 32px;
  width: 36px;
  margin: 0 auto !important;
  border-radius: 999px;
  padding: 0;
}

.sidenav-desktop.is-collapsed .sidenav-header {
  padding-left: 0.35rem;
  padding-right: 0.35rem;
}

.sidenav-desktop.is-collapsed .sidenav-header a {
  padding: 0.4rem 0 !important;
}

.sidenav-desktop.is-collapsed .org-header-logo-frame {
  min-height: 3rem;
}

.sidenav-desktop.is-collapsed .sidenav-header img {
  max-width: 42px !important;
}

.sidenav-desktop.is-collapsed .nav-link {
  justify-content: center !important;
  padding-left: 0.35rem !important;
  padding-right: 0.35rem !important;
}

.sidenav-desktop.is-collapsed .nav-icon-wrapper {
  width: 100% !important;
  margin: 0 !important;
  display: inline-flex;
  justify-content: center;
}

.org-switcher-logo {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  object-fit: cover;
  flex: 0 0 auto;
}

.org-switcher-toggle {
  min-width: 30px;
  width: 30px;
  height: 30px;
  border-radius: 0.45rem;
  padding: 0;
  flex: 0 0 auto;
  margin: 0 !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.org-switcher-menu {
  position: fixed;
  background: rgba(44, 50, 58, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 0.6rem;
  box-shadow: 0 14px 32px rgba(15, 23, 42, 0.35);
  padding: 0.25rem 0;
  z-index: 20;
  overflow-x: hidden;
}

.sidenav-admin .org-switcher-menu {
  background: rgba(23, 35, 59, 0.97);
}

.sidenav-default .org-switcher-menu {
  background: rgba(44, 50, 58, 0.98);
}

.org-switcher-divider {
  height: 1px;
  margin: 0.25rem 0.7rem;
  background: rgba(255, 255, 255, 0.15);
}

.org-switcher-item {
  width: 100%;
  margin: 0 !important;
  border-radius: 0 !important;
  color: rgba(255, 255, 255, 0.9) !important;
  padding: 0.45rem 0.7rem !important;
  text-decoration: none !important;
  transition: background-color 0.16s ease, transform 0.16s ease, color 0.16s ease;
}

.org-switcher-item:hover {
  background: rgba(255, 255, 255, 0.1) !important;
  transform: translateX(3px);
  color: #fff !important;
}

.org-switcher-item.is-active {
  background: rgba(247, 142, 71, 0.22) !important;
  color: #fff !important;
  font-weight: 700;
}

.org-menu-enter-active,
.org-menu-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.org-menu-enter-from,
.org-menu-leave-to {
  opacity: 0;
  transform: translateX(-8px);
}

.org-menu-enter-to,
.org-menu-leave-from {
  opacity: 1;
  transform: translateX(0);
}

.mobile-sidenav-toggle {
  position: fixed;
  left: 0.85rem;
  bottom: 0.85rem;
  z-index: 1100;
  width: 44px;
  height: 44px;
  border-radius: 999px;
  display: none;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.2);
  transition: left 0.2s ease-in-out, transform 0.2s ease-in-out;
}

.mobile-sidenav-toggle.is-shifted {
  left: 15.75rem;
}

@media (max-width: 1199px) {
  .sidenav-mobile {
    width: 15rem;
    min-width: 15rem;
    max-width: 15rem;
    z-index: 1080;
    box-shadow: 8px 0 22px rgba(15, 23, 42, 0.22);
  }

  .mobile-sidenav-toggle {
    display: inline-flex;
    z-index: 1200;
  }
}

.mobile-sidenav-enter-active,
.mobile-sidenav-leave-active {
  transition: transform 0.2s ease-in-out, opacity 0.2s ease-in-out;
}

.mobile-sidenav-enter-from,
.mobile-sidenav-leave-to {
  transform: translateX(-12px);
  opacity: 0;
}

.org-switcher-collapsed {
  justify-content: center;
  padding-left: 0.35rem;
  padding-right: 0.35rem;
}

.org-switcher-collapsed .org-switcher-toggle {
  width: 34px;
  min-width: 34px;
  height: 34px;
}


</style>
